/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Created On: March 23rd, 2018
 * Last Modified: March 23rd, 2018
 * ****************************************************************************
 */

import * as React from 'react';
import * as Mui from 'mui';
import * as deepEqual from 'deep-equal';
import { SetupGameViewProps } from '..';
import { Dimensions } from 'components/board/chessTypes';
import { GameState, UriGameState, GameStateContextProvider } from 'contexts/gameStateContext';
import { AppEvents } from 'contexts/appEventsContext';
import renderChessPiece from 'app/renderChessPiece';
import { breakpoints } from 'app/breakpoints';
import { SetupBoard } from './setupBoard';
import { GameOptions } from './gameOptions';
import FenParser from '@chess-fu/fen-parser';
import { PopupMenuItem } from 'components';
import { Clipboard } from 'components/clipboard';
import ChessGame, { Move } from '@chess-fu/chess-game';

/** SetupGameProps - Properties */
export interface SetupGameProps extends SetupGameViewProps {
  bounds: Dimensions;
  game: GameState;
  events: AppEvents;
}

export interface SetupGameState {
  currentUri?: string;
  fen: string;
  side: 'w' | 'b';
  unsubscribe?: () => void;
}

/** SetupGame */
export class SetupGame extends React.Component<SetupGameProps, SetupGameState> {
  constructor(props: SetupGameProps, ctx: any) {
    super(props, ctx);

    this.state = {
      currentUri: '',
      fen: '',
      side: 'w',
    };
  }

  componentDidMount() {
    const subscriptions = [
      this.props.game.onChange((arg) => this.onGameChange(arg)),
      this.props.events.onMenu('play-game', this.play),
      this.props.events.onBuildMenu(this.buildMenu),
    ];

    this.setState({
      unsubscribe: () => { subscriptions.forEach(fn => fn()); }
    });
  }

  componentWillUnmount() {
    if (this.state.unsubscribe) {
      this.state.unsubscribe();
    }
  }

  syncUri(state: SetupGameState) {
    const uriData: UriGameState = {
      fen: state.fen,
      play: {},
      options: { side: state.side }
    };

    const url = this.props.match.path + GameState.toUri(uriData);
    this.props.history.push(url);
  }

  onGameChange(arg: { fen: string }) {
    if (this.state.fen !== arg.fen) {
      console.info('changed:', arg);
      const state = { fen: arg.fen, side: this.state.side };
      this.setState(state);
      this.syncUri(state);
    }
  }

  static getDerivedStateFromProps(props: SetupGameProps, state: SetupGameState) {
    const { pathname, search } = props.location;
    const path = pathname.substr(props.match.path.length) || 'start';
    const currentUri = `${path}?${search}`;
    if (currentUri === state.currentUri) {
      return null;
    }

    const uriData = GameState.fromUri(path, props.location.search);
    if (uriData.fen !== state.fen) {
      props.game.setGameState({ fen: uriData.fen, play: {} });
    }

    const stateChange: any = { currentUri };
    if (!deepEqual(uriData.options, { side: state.side })) {
      stateChange.side = uriData.options.side || stateChange.side;
    }

    return stateChange;
  }

  readonly play = () => {
    const fenUrl = '/play/' + this.props.game.fen();
    this.props.history.push(fenUrl);
  }

  readonly onPaste = (data: { value: string }) => {
    const fen = data.value;
    if (FenParser.isFen(fen)) {
      const fenUrl = this.props.match.path + fen.replace(/ /g, '.');
      this.props.history.push(fenUrl);
    }
    else {
      const tmpGame = new ChessGame();
      try { tmpGame.loadPgn(data.value); }
      catch (ex) {
        console.warn('bad game:', {
          head: tmpGame.header(),
          fen: tmpGame.fen(),
        });
        return alert(ex.message);
      }

      console.warn('game:', {
        head: tmpGame.header(),
        fen: tmpGame.fen(),
      });

      const endingFen = tmpGame.fen();
      const fenUrl = this.props.match.path + endingFen.replace(/ /g, '.');
      this.props.history.push(fenUrl);
    }
  }

  readonly clearAll = () => {
    const fenUrl = this.props.match.path + 'empty';
    this.props.history.push(fenUrl);
  }

  readonly startPos = () => {
    const fenUrl = this.props.match.path + 'start';
    this.props.history.push(fenUrl);
  }

  readonly setPlayer = (player: string) => {
    this.props.game.setPlayer(player);
  }

  readonly setCastles = (castles: string) => {
    this.props.game.setCastles(castles);
  }

  readonly flipBoard = () => {
    let { side } = this.state;
    side = side === 'b' ? 'w' : 'b';
    const state = { fen: this.state.fen, side };
    this.setState(state);
    this.syncUri(state);
  }

  readonly buildMenu = (menu: PopupMenuItem[]) => {
    menu.push(
      { icon: 'play_arrow', text: 'Play from here', action: this.play },
      { icon: 'library_books', text: 'Copy FEN', action: this.props.events.menuClick('copy') },
      { icon: 'assignment_returned', text: 'Paste FEN/PGN', action: this.props.events.menuClick('paste') }
    );
  };

  readonly optionsMenu: PopupMenuItem[] = [
    { icon: 'swap_vert', text: 'Flip Board', action: this.flipBoard },
    { icon: 'clear', text: 'Clear board', action: this.clearAll },
    { icon: 'settings_backup_restore', text: 'Start position', action: this.startPos }
  ];

  readonly onMovePiece = (move: Move) => {
    const { game } = this.props;
    const changes: any = {};
    changes[move.from] = null;
    changes[move.to] = game.getPiece(move.from);
    game.apply(changes);
  }

  readonly onPlacePiece = (square: string, piece: string | null) => {
    const { game } = this.props;
    const changes: any = {};
    changes[square] = piece;
    game.apply(changes);
  }

  render() {
    const { bounds, game } = this.props;
    const { side } = this.state;

    const isPortrait = bounds.height > (bounds.width + 60);
    const szOptionsPane = {
      width: isPortrait ? 0 : 150,
      height: isPortrait ? 100 : 0,
    };

    const szAvailable = {
      height: bounds.height - szOptionsPane.height - 2,
      width: bounds.width - szOptionsPane.width - 2,
    };

    let horzMargin = bounds.width >= breakpoints.sm ? 16 : 8;
    let vertMargin = bounds.height >= breakpoints.sm ? 16 : 8;

    const minSizeXY = Math.floor(Math.min(szAvailable.width - (horzMargin * 2), szAvailable.height - (vertMargin * 2)));
    const boardSize = { height: minSizeXY, width: minSizeXY };
    horzMargin = Math.min(16, (szAvailable.width - boardSize.width) / 2);
    vertMargin = Math.min(16, (szAvailable.height - boardSize.height) / 2);
    const margins = { width: horzMargin, height: vertMargin };

    return (
      <GameStateContextProvider value={this.props.game}>
        <Mui.Paper className="background-color fill column" style={{ margin: `0` }}>
          <Clipboard events={this.props.events} value={game.fen()} onPaste={this.onPaste} />
          <div className="flex row no-wrap top">
            <SetupBoard
              style={{ margin: `${vertMargin}px ${horzMargin}px` }}
              flipBoard={side === 'b'}
              boardSize={boardSize}
              fen={game.fen()}
              renderPiece={renderChessPiece}
              onMovePiece={this.onMovePiece}
              onPlacePiece={this.onPlacePiece}
            />
            {!isPortrait && this.renderGameOptions(isPortrait, margins, szAvailable)}
          </div>
          {isPortrait && this.renderGameOptions(isPortrait, margins, szAvailable)}
        </Mui.Paper>
      </GameStateContextProvider >
    );
  }

  renderGameOptions(isPortrait: boolean, margins: Dimensions, szAvailable: Dimensions) {

    const { width: horzMargin, height: vertMargin } = margins;
    const optionsStyle: React.CSSProperties = {
      flexGrow: 1,
    };
    if (isPortrait) {
      optionsStyle.marginTop = 8 > vertMargin ? (8 - vertMargin) : 0;
      optionsStyle.marginBottom = vertMargin;
      optionsStyle.marginLeft = horzMargin;
      optionsStyle.marginRight = horzMargin;
    }
    else {
      optionsStyle.marginLeft = 8 > horzMargin ? (8 - horzMargin) : 0;
      optionsStyle.marginRight = horzMargin;
      optionsStyle.marginTop = vertMargin;
      optionsStyle.marginBottom = vertMargin;
      optionsStyle.height = szAvailable.height - (vertMargin * 2);
    }

    return (
      <div style={optionsStyle}>
        <GameOptions
          fen={this.props.game.fen()}
          onPlay={this.play}
          optionsMenu={this.optionsMenu}
          onSetPlayer={this.setPlayer}
          onSetCastles={this.setCastles}
          renderPiece={renderChessPiece}
        />
      </div>
    );
  }
}

export default SetupGame;


// WEBPACK FOOTER //
// ./src/views/setupGame/components/setupGame.tsx